/**
 * Utilitários para validação e manipulação de arquivos SVG
 */

/**
 * Verifica se uma string contém um SVG válido
 */
export const isValidSVG = (svgString: string): boolean => {
  if (!svgString || typeof svgString !== 'string') {
    return false
  }

  // Remover espaços em branco para facilitar validação
  const trimmedSvg = svgString.trim()

  // Verificar se contém tag svg de abertura e fechamento
  if (!trimmedSvg.includes('<svg') || !trimmedSvg.includes('</svg>')) {
    return false
  }

  // Verificar se é data URL de SVG
  if (trimmedSvg.startsWith('data:image/svg+xml')) {
    return true
  }

  // Verificar se é URL que termina com .svg
  if (trimmedSvg.endsWith('.svg')) {
    return true
  }

  // Verificar se DOMParser está disponível (browser environment)
  if (typeof DOMParser !== 'undefined') {
    try {
      // Tentar fazer parse básico para verificar se é XML válido
      const parser = new DOMParser()
      const doc = parser.parseFromString(trimmedSvg, 'image/svg+xml')
      
      // Verificar se não há erros de parse
      const parseError = doc.querySelector('parsererror')
      if (parseError) {
        return false
      }

      // Verificar se realmente é um elemento SVG
      const svgElement = doc.querySelector('svg')
      return svgElement !== null

    } catch (error) {
      console.warn('Erro na validação SVG:', error)
      return false
    }
  }

  // Fallback para ambientes sem DOMParser (como Node.js)
  // Validação básica usando regex
  const svgRegex = /<svg[^>]*>[\s\S]*<\/svg>/i
  return svgRegex.test(trimmedSvg)
}

/**
 * Sanitiza e otimiza um SVG para uso seguro
 */
export const sanitizeSVG = (svgString: string): string => {
  if (!isValidSVG(svgString)) {
    return ''
  }

  let sanitized = svgString

  // Remover scripts maliciosos
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
  
  // Remover event handlers
  sanitized = sanitized.replace(/on\w+="[^"]*"/g, '')
  sanitized = sanitized.replace(/on\w+='[^']*'/g, '')
  
  // Garantir que tenha viewBox se não tiver width/height
  if (!sanitized.includes('viewBox') && !sanitized.includes('width=') && !sanitized.includes('height=')) {
    sanitized = sanitized.replace(
      /<svg([^>]*)>/,
      '<svg$1 viewBox="0 0 24 24">'
    )
  }

  return sanitized
}

/**
 * Aplica customizações básicas a um SVG
 */
export const customizeSVG = (
  svgString: string,
  options: {
    size?: number
    strokeWidth?: number
    strokeColor?: string
    fillColor?: string
    backgroundColor?: string
  }
): string => {
  let customized = svgString

  // Ajustar tamanho do SVG se especificado
  if (options.size !== undefined) {
    // Remover width e height existentes e adicionar novos
    customized = customized.replace(/width="[^"]*"/g, '')
    customized = customized.replace(/height="[^"]*"/g, '')
    customized = customized.replace(
      /<svg([^>]*)>/,
      `<svg$1 width="${options.size}" height="${options.size}">`
    )
    
    // Garantir que o SVG use o viewBox para escalar corretamente
    if (!customized.includes('viewBox')) {
      customized = customized.replace(
        /<svg([^>]*)>/,
        `<svg$1 viewBox="0 0 24 24">`
      )
    }
  }

  if (options.strokeWidth !== undefined) {
    // Substituir stroke-width em todos os elementos que já têm esta propriedade
    customized = customized.replace(
      /stroke-width="[^"]*"/g,
      `stroke-width="${options.strokeWidth}"`
    )
    
    // Aplicar stroke-width em elementos específicos que podem ter contornos
    const elementsWithStroke = ['path', 'circle', 'ellipse', 'rect', 'line', 'polyline', 'polygon']
    
    elementsWithStroke.forEach(element => {
      // Para elementos que já têm stroke mas não têm stroke-width
      const regex = new RegExp(`(<${element}[^>]*stroke="[^"]*"[^>]*?)(?!.*stroke-width)([^>]*>)`, 'gi')
      customized = customized.replace(regex, `$1 stroke-width="${options.strokeWidth}"$2`)
      
      // Para elementos que têm stroke definido em style
      const styleRegex = new RegExp(`(<${element}[^>]*style="[^"]*stroke:[^;]*?)(?!.*stroke-width)([^"]*"[^>]*>)`, 'gi')
      customized = customized.replace(styleRegex, `$1;stroke-width:${options.strokeWidth}$2`)
    })
    
    // Se não encontrou elementos específicos com stroke, aplicar no SVG root
    if (!customized.match(/stroke-width=/) && !customized.includes('stroke-width:')) {
      customized = customized.replace(
        /<svg([^>]*)>/,
        `<svg$1 stroke-width="${options.strokeWidth}">`
      )
    }
  }

  if (options.strokeColor) {
    if (options.strokeColor !== 'transparent') {
      customized = customized.replace(
        /stroke="[^"]*"/g,
        `stroke="${options.strokeColor}"`
      )
      if (!customized.includes('stroke=')) {
        customized = customized.replace(
          /<svg([^>]*)>/,
          `<svg$1 stroke="${options.strokeColor}">`
        )
      }
    } else {
      customized = customized.replace(/stroke="[^"]*"/g, 'stroke="none"')
    }
  }

  if (options.fillColor) {
    if (options.fillColor !== 'transparent') {
      customized = customized.replace(
        /fill="[^"]*"/g,
        `fill="${options.fillColor}"`
      )
      if (!customized.includes('fill=')) {
        customized = customized.replace(
          /<svg([^>]*)>/,
          `<svg$1 fill="${options.fillColor}">`
        )
      }
    } else {
      customized = customized.replace(/fill="[^"]*"/g, 'fill="none"')
    }
  }

  if (options.backgroundColor && options.backgroundColor !== 'transparent') {
    const rectBg = `<rect width="100%" height="100%" fill="${options.backgroundColor}"/>`
    customized = customized.replace(
      /(<svg[^>]*>)/,
      `$1${rectBg}`
    )
  }

  return customized
}

/**
 * Converte arquivo SVG em base64 data URL
 */
export const svgToDataURL = (svgString: string): string => {
  const sanitized = sanitizeSVG(svgString)
  if (!sanitized) {
    return ''
  }
  
  const encoded = encodeURIComponent(sanitized)
  return `data:image/svg+xml,${encoded}`
}

/**
 * Extrai informações básicas de um SVG
 */
export const getSVGInfo = (svgString: string): {
  width?: number
  height?: number
  viewBox?: string
  hasStrokes?: boolean
  hasFills?: boolean
} => {
  if (!isValidSVG(svgString)) {
    return {}
  }

  const info: any = {}

  // Extrair width
  const widthMatch = svgString.match(/width="([^"]+)"/i)
  if (widthMatch) {
    const width = parseFloat(widthMatch[1])
    if (!isNaN(width)) {
      info.width = width
    }
  }

  // Extrair height
  const heightMatch = svgString.match(/height="([^"]+)"/i)
  if (heightMatch) {
    const height = parseFloat(heightMatch[1])
    if (!isNaN(height)) {
      info.height = height
    }
  }

  // Extrair viewBox
  const viewBoxMatch = svgString.match(/viewBox="([^"]+)"/i)
  if (viewBoxMatch) {
    info.viewBox = viewBoxMatch[1]
  }

  // Verificar se tem strokes
  info.hasStrokes = svgString.includes('stroke=') || svgString.includes('stroke:')

  // Verificar se tem fills
  info.hasFills = svgString.includes('fill=') || svgString.includes('fill:')

  return info
}

/**
 * Extrai as cores atuais do SVG
 */
export const extractSVGColors = (svgString: string): {
  strokeColor: string
  fillColor: string
  strokeWidth: number
} => {
  const defaults = {
    strokeColor: '#000000',
    fillColor: '#000000', 
    strokeWidth: 1
  }

  if (!isValidSVG(svgString)) {
    return defaults
  }

  // Extrair cor do stroke
  let strokeColor = defaults.strokeColor
  const strokeMatch = svgString.match(/stroke="([^"]+)"/i) || svgString.match(/stroke:\s*([^;]+)/i)
  if (strokeMatch && strokeMatch[1]) {
    const color = strokeMatch[1].trim()
    if (color === 'none' || color === 'transparent' || color === '') {
      strokeColor = 'transparent'
    } else {
      strokeColor = color
    }
  }

  // Extrair cor do fill
  let fillColor = defaults.fillColor
  const fillMatch = svgString.match(/fill="([^"]+)"/i) || svgString.match(/fill:\s*([^;]+)/i)
  if (fillMatch && fillMatch[1]) {
    const color = fillMatch[1].trim()
    if (color === 'none' || color === 'transparent' || color === '') {
      fillColor = 'transparent'
    } else {
      fillColor = color
    }
  }

  // Se não encontrou cores explícitas, verificar elementos filhos
  if ((strokeColor === defaults.strokeColor || strokeColor === 'transparent') && 
      (fillColor === defaults.fillColor || fillColor === 'transparent')) {
    // Procurar por qualquer elemento com cor
    const pathStrokeMatch = svgString.match(/<path[^>]*stroke="([^"]+)"[^>]*>/i) || 
                           svgString.match(/<circle[^>]*stroke="([^"]+)"[^>]*>/i) ||
                           svgString.match(/<rect[^>]*stroke="([^"]+)"[^>]*>/i) ||
                           svgString.match(/<line[^>]*stroke="([^"]+)"[^>]*>/i)
    
    if (pathStrokeMatch && pathStrokeMatch[1]) {
      const color = pathStrokeMatch[1].trim()
      if (color !== 'none' && color !== 'transparent') {
        strokeColor = color
      } else {
        strokeColor = 'transparent'
      }
    }
    
    const pathFillMatch = svgString.match(/<path[^>]*fill="([^"]+)"[^>]*>/i) ||
                         svgString.match(/<circle[^>]*fill="([^"]+)"[^>]*>/i) ||
                         svgString.match(/<rect[^>]*fill="([^"]+)"[^>]*>/i)
    
    if (pathFillMatch && pathFillMatch[1]) {
      const color = pathFillMatch[1].trim()
      if (color !== 'none' && color !== 'transparent') {
        fillColor = color
      } else {
        fillColor = 'transparent'
      }
    }
  }

  // Se ainda não encontrou cores, usar valores padrão mais apropriados
  if (strokeColor === defaults.strokeColor && fillColor === defaults.fillColor) {
    // Se não há cores definidas explicitamente, assumir que é um ícone com fill preto
    fillColor = '#000000'
    strokeColor = 'none' // Usar 'none' em vez de 'transparent' para ser mais preciso ao SVG
  }

  // Normalizar valores none/transparent
  if (strokeColor === 'none') {
    strokeColor = 'transparent'
  }
  if (fillColor === 'none') {
    fillColor = 'transparent'
  }

  // Extrair stroke-width
  let strokeWidth = defaults.strokeWidth
  const strokeWidthMatch = svgString.match(/stroke-width="([^"]+)"/i) || svgString.match(/stroke-width:\s*([^;]+)/i)
  if (strokeWidthMatch && strokeWidthMatch[1]) {
    const width = parseFloat(strokeWidthMatch[1])
    if (!isNaN(width) && width >= 0) {
      strokeWidth = width
    }
  } else {
    // Procurar stroke-width em elementos filhos
    const pathStrokeWidthMatch = svgString.match(/<path[^>]*stroke-width="([^"]+)"[^>]*>/i) ||
                                svgString.match(/<circle[^>]*stroke-width="([^"]+)"[^>]*>/i) ||
                                svgString.match(/<rect[^>]*stroke-width="([^"]+)"[^>]*>/i) ||
                                svgString.match(/<line[^>]*stroke-width="([^"]+)"[^>]*>/i)
    
    if (pathStrokeWidthMatch && pathStrokeWidthMatch[1]) {
      const width = parseFloat(pathStrokeWidthMatch[1])
      if (!isNaN(width) && width >= 0) {
        strokeWidth = width
      }
    }
  }

  return {
    strokeColor,
    fillColor,
    strokeWidth
  }
}